import { useMediaQuery, useTheme } from '@mui/material';
import type { GridColumnVisibilityModel, GridRowParams } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { NetworkAuditLogs__AuditLog as AuditLog } from '~/components/AuditLogs/queries/list.generated';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import AuditLogsIllustration from '~/images/illustrations/audit-logs.svg';
import { useColumns } from '../lib';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={AuditLogsIllustration}
      description={t('pages.audit_logs.empty_state')}
    />
  );
};
export interface TableProps {
  auditLogs: readonly AuditLog[];
  loading: boolean;
}

export const Table = ({ auditLogs, loading }: TableProps) => {
  const navigate = useNavigate();

  const handleViewDetails = useCallback(
    (row: AuditLog) => {
      navigate(`${row.id}/details`);
    },
    [navigate],
  );

  const columns = useColumns(handleViewDetails);
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      createdAt: !isSmallAndDown,
      actions: !isSmallAndDown,
    }));
  }, [isSmallAndDown]);

  return (
    <ScrollableDataGrid
      checkboxSelection={false}
      columns={columns}
      columnVisibilityModel={showColumns}
      disableRowSelectionOnClick
      loading={loading}
      onColumnVisibilityModelChange={setShowColumns}
      onRowClick={(params: GridRowParams<AuditLog>) => handleViewDetails(params.row)}
      rows={auditLogs}
      slots={{
        columnResizeIcon: () => null,
        noRowsOverlay: NoRowsOverlay,
      }}
    />
  );
};
